'use client'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { downloadAuthenticatedFile, DownloadError, formatBytes } from '@/lib/ai/download'
import type { ChatAttachment, FileAttachmentData } from '@/lib/ai/types'

/**
 * "file" attachment — a generated PDF. Download goes through an authenticated
 * fetch (bearer token) → blob → <a download>, never a plain href.
 */
export function FileCard({ attachment }: { attachment: ChatAttachment }) {
  const data = (attachment.data ?? {}) as FileAttachmentData
  const [busy, setBusy] = useState(false)
  const filename = data.filename || attachment.url?.split('/').pop() || 'report.pdf'
  const size = formatBytes(data.size_bytes)

  const handleDownload = async () => {
    if (!attachment.url || busy) return
    setBusy(true)
    try {
      await downloadAuthenticatedFile(attachment.url, filename)
      toast.success('Download started', { description: filename })
    } catch (err) {
      if (err instanceof DownloadError && err.status === 401) {
        toast.error('Session expired', { description: 'Please sign in again to download this file.' })
      } else if (err instanceof DownloadError && err.status === 404) {
        toast.error('File not found', { description: 'This report is no longer available. Ask me to regenerate it.' })
      } else {
        toast.error('Download failed', { description: err instanceof Error ? err.message : 'Please try again.' })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={cn('flex items-center gap-3 rounded-xl border border-border/50 bg-white px-3 py-2.5 shadow-soft')}>
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
        <Icons.fileText className="h-4 w-4" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-brand-navy">{attachment.title || filename}</p>
        <p className="truncate text-xs text-muted-foreground">
          {[filename, size, data.report_type ? data.report_type.replace(/_/g, ' ') : null].filter(Boolean).join(' · ')}
        </p>
        {attachment.description && <p className="mt-0.5 text-xs text-muted-foreground">{attachment.description}</p>}
      </div>
      <Button size="sm" variant="outline" onClick={handleDownload} disabled={busy || !attachment.url} className="flex-shrink-0 gap-1.5">
        {busy ? <Icons.loader className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <Icons.download className="h-3.5 w-3.5" strokeWidth={1.5} />}
        {busy ? 'Fetching…' : 'Download'}
      </Button>
    </div>
  )
}
